import {
  FilterFilled,
  LoadingOutlined,
  PlusOutlined,
  ProfileOutlined,
  UndoOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  message,
  Modal,
  Select,
  Alert,
  Divider,
  Statistic,
  Table,
  Tag,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { get } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  ArrayParam,
  BooleanParam,
  NumberParam,
  NumericArrayParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';

import TableFilterAdvanced from '../../../components/TableFilterAdvanced';
import TableFilterSearch, {
  filterSearchIcon,
} from '../../../components/TableFilterSearch';
import MultiKeywordSearch from '../../../components/MultiKeywordSearch';
import XeroSyncButton from '../../../components/XeroSyncButton';
import XeroSyncHistory from '../../../components/XeroSyncHistory';
import { URL_ORDER_SYNC_TO_XERO } from '../../../constants/URLS';
import { useSAUsers } from '../../../custom-hooks/sa-users';
import { queryClient } from '../../../QueryClient';
import { fetchAPI } from '../../../utils/api';
import {
  formatPrice,
  formatUTCTimeToLocal,
  getCurrencyFromRegion,
} from '../../../utils/format';
import useWindowDimension from '../../../utils/useWindowDimension';
import { safelyParseJSON } from '../../Ticketing/SubmittedTicketList';
import EditInvoiceDateInTable from '../EditInvoiceDateInTable';
import EditDoDateInTable from '../EditDoDateInTable';
import OrderDoDateHistory from '../OrderDoDateHistory';
import OrderCreate from '../OrderCreate';
import OrderInvoiceDateHistory from '../OrderInvoiceDateHistory';
import { getOrderDetail } from '../OrderListing';
import OrderPaymentDate from '../OrderPaymentDate';
import OrderPaymentStatusDropdown from '../OrderPaymentStatus';
import OrderSendInvoice from '../OrderSendInvoice';
import OrderSendReportMenu from '../OrderSendReportMenu';
import OrderShipmentStatus from '../OrderShipmentStatus';
import OrderTotalPOValues, {
  DropdownCurrency,
  OrderFXRates,
  OrderTotalClientPaidValues,
  OrderTotalPOFulfilledValues,
  TotalPOProvider,
} from '../OrderTotalPOValues';
import { ColorMap, formatOrder } from '../utils';
import { OrderOperationStatus, OrderPaymentStatus } from './types';
import DownloadOrderItems from './DownloadOrderItems';
import DownloadOrders from './DownloadOrders';
import OrderActionColumn from './OrderActionColumn';
import AmazonOrderDeliveryStatus from './AmazonOrderDeliveryStatus';
import OrderItemV2Table from './OrderItemsV2';
import ReorderColumnsOrderV2, {
  defaultOrderColumns,
  OrderColumn,
} from './ReorderColumnsOrderV2';
import { OrderV2 } from './types';
import UploadSpidReport from './UploadSpidReport';
import OrderEmailOrderPicker from '../OrderEmailOrderPicker';
import OrderExternalRemittance from '../OrderExternalRemittance';
import OrderModalMapExternalOrder from '../OrderModalMapExternalOrder';
import OrderModalMismapOrder from '../OrderModalMismapOrder';
import { primaryColor } from '../../../constants/COLORS';
import { FilterDropdownProps, SorterResult } from 'antd/lib/table/interface';
import { useAllEntityGroups } from '../../../custom-hooks/sa-entities';
import OrderCorrectedEmailReader from '../OrderCorrectedEmailReader';
import DownloadBulkInvoice from '../DownloadBulkInvoice';
import InvoiceConsolidation from '../InvoiceConsolidation';
import ShippingAddressFromER from './ShippingAddressFromER';

const orderV2ColumnsKey = 'order-v2-columns-order';

export const useOrderV2QueryParams = () => {
  return useQueryParams({
    q: withDefault(StringParam, ''),
    page: withDefault(NumberParam, 1),
    per_page: withDefault(NumberParam, 5),
    date_start: withDefault(StringParam, ''),
    date_end: withDefault(StringParam, ''),
    is_internal_purchase: BooleanParam,
    filter_region: withDefault(ArrayParam, []),
    filter_operation_status: withDefault(ArrayParam, []),
    filter_exclude_operation_status: withDefault(ArrayParam, []),
    filter_payment_status: withDefault(ArrayParam, []),
    filter_is_consolidated_line_items: withDefault(ArrayParam, []),
    filter_created_by: withDefault(NumericArrayParam, []),
    filter_cumulative_margin: withDefault(NumericArrayParam, []),
    filter_buyer_name: withDefault(NumericArrayParam, []),
    filter_entity_name: withDefault(NumericArrayParam, []),
    filter_entity_group: withDefault(ArrayParam, []),
    filter_requester_name: withDefault(NumericArrayParam, []),
    filter_paid_by: withDefault(StringParam, ''),
    filter_shipping_address: withDefault(ArrayParam, []),
    q_assignee: withDefault(ArrayParam, []), // Advance Filters
    is_without_po: BooleanParam, // Advance Filters
    is_negative_margin: BooleanParam, // Advance Filters
    is_ncpr: BooleanParam, // Advance Filters
    filter_xero_sync_status: StringParam, // Advance Filters
    filter_exclude_management_fee_only: BooleanParam, // Advance Filters
    has_change_reason: BooleanParam, // Advance Filters
    sort_key: StringParam,
    sort_order: StringParam,
    filter_is_corrected: StringParam,
    date_type: withDefault(StringParam, 'created_at'),
    q_journal_number: withDefault(StringParam, ''),
  });
};

export const makeOrderListV2Payload = qp => {
  const filters = Object.keys(qp).reduce((obj, qpKey) => {
    if (qpKey.startsWith('filter_')) {
      const arr = qp[qpKey];
      if (!!arr && !!arr.length) {
        return { ...obj, [qpKey.replace('filter_', '')]: arr };
      }
    }
    if (
      qpKey === 'has_change_reason' &&
      typeof qp.has_change_reason === 'boolean'
    ) {
      return { ...obj, has_change_reason: qp.has_change_reason };
    }
    return obj;
  }, {});
  const sorter = {
    columnKey: qp.sort_key,
    order: qp.sort_order,
  };
  return {
    page: qp.page,
    per_page: qp.per_page,
    q: qp.q,
    q_assignee: qp.q_assignee.join(''),
    q_journal_number: qp.q_journal_number,
    is_internal_purchase:
      typeof qp.is_internal_purchase === 'boolean'
        ? qp.is_internal_purchase
        : undefined,
    from_date: !!qp.date_start ? qp.date_start : undefined,
    to_date: !!qp.date_end ? qp.date_end : undefined,
    date_type: qp.date_type,
    is_without_po: qp.is_without_po,
    is_negative_margin: qp.is_negative_margin,
    is_ncpr: qp.is_ncpr,
    filter_xero_sync_status: qp.filter_xero_sync_status,
    filters,
    sorter: !!qp.sort_key ? sorter : null,
  };
};

const operationStatusFilters = [
  OrderOperationStatus.ORDER_CREATED,
  OrderOperationStatus.AUTO_CREATE_SPID,
  OrderOperationStatus.PENDING_APPROVAL,
  OrderOperationStatus.APPROVED,
  OrderOperationStatus.REJECTED,
  OrderOperationStatus.PENDING_REJECTION,
  OrderOperationStatus.PENDING_REJECTED,
  OrderOperationStatus.PENDING_DELIVERY,
  OrderOperationStatus.PENDING_DO_UPLOAD,
  OrderOperationStatus.UPLOAD_SIGN_DO,
  OrderOperationStatus.PENDING_SIGN_DO_UPLOAD,
  OrderOperationStatus.COMPLETED,
  OrderOperationStatus.COMPLETED_TENTATIVE,
  OrderOperationStatus.PO_CANCELLED,
  OrderOperationStatus.CANCELLED_FROM_MP,
  OrderOperationStatus.OOS,
];

const TriStateOperationStatusFilter = (
  props: FilterDropdownProps & {
    includeValues: string[];
    excludeValues: string[];
    onApply: (include: string[], exclude: string[]) => void;
  }
) => {
  const [include, setInclude] = useState<string[]>(props.includeValues);
  const [exclude, setExclude] = useState<string[]>(props.excludeValues);
  const { visible } = props;

  useEffect(() => {
    if (visible) {
      setInclude(props.includeValues);
      setExclude(props.excludeValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const cycle = (status: string) => {
    // none -> include -> exclude -> none
    if (include.includes(status)) {
      setInclude(include.filter(v => v !== status));
      setExclude([...exclude, status]);
    } else if (exclude.includes(status)) {
      setExclude(exclude.filter(v => v !== status));
    } else {
      setInclude([...include, status]);
    }
  };

  return (
    <div style={{ padding: 8, width: 240 }}>
      <div className="m-label mb-1">Filter Operation Status</div>
      <div>
        {operationStatusFilters.map(status => {
          const isInclude = include.includes(status);
          const isExclude = exclude.includes(status);
          return (
            <div
              key={status}
              onClick={() => cycle(status)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '4px 8px',
                marginBottom: 4,
                borderRadius: 4,
                cursor: 'pointer',
                background: isInclude
                  ? 'rgba(82, 196, 26, 0.15)'
                  : isExclude
                  ? 'rgba(255, 77, 79, 0.15)'
                  : undefined,
              }}
            >
              <span
                style={{
                  textDecoration: isExclude ? 'line-through' : undefined,
                }}
              >
                {status}
              </span>
              <span style={{ fontSize: 12 }}>
                {isInclude ? 'Include' : isExclude ? 'Exclude' : ''}
              </span>
            </div>
          );
        })}
      </div>
      <div
        style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}
      >
        <Button
          size="small"
          style={{ marginRight: 8 }}
          onClick={() => {
            props.setSelectedKeys([]);
            props.clearFilters();
            props.onApply([], []);
          }}
        >
          Reset
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={() => {
            props.setSelectedKeys(include);
            props.onApply(include, exclude);
            props.confirm();
          }}
        >
          OK
        </Button>
      </div>
    </div>
  );
};

const paymentStatusFilters = [
  OrderPaymentStatus.PENDING_INVOICE_SUBMISSION,
  OrderPaymentStatus.INVOICE_DISPUTED,
  OrderPaymentStatus.PARTIALLY_PAID,
  OrderPaymentStatus.PAID,
  OrderPaymentStatus.REFUNDED,
  OrderPaymentStatus.PENDING_PAYMENT,
];

const OrderListingV2 = () => {
  const [queryParams, setQueryParams] = useOrderV2QueryParams();
  const user = useSelector((state: AppState) => state.user);
  const users = useSAUsers(user);
  const entityGroups = useAllEntityGroups();

  const [loading, setLoading] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState<string[]>([]);
  const [orderDrawer, setOrderDrawer] = useState(false);
  const [orderId, setOrderId] = useState(0);
  const [columns, setColumns] = useState(defaultOrderColumns);
  const [formOrder] = Form.useForm();
  const { isSmallScreen } = useWindowDimension();
  const [showAdvanceFilters, setShowAdvanceFilters] = useState(false);
  const [
    openModalDownloadBulkInvoice,
    setOpenModalDownloadBulkInvoice,
  ] = useState(false);
  const [invoiceConsolidationDrawer, setInvoiceConsolidationDrawer] = useState(
    false
  );
  const orders = useQuery<{
    items: OrderV2[];
    page: number;
    per_page: number;
    total: number;
    loading: boolean;
    status: unknown;
  }>(
    ['order-list-v2', queryParams],
    async () => {
      try {
        const resp = await fetchAPI('order/list', {
          method: 'POST',
          version: '2.0',
          payload: makeOrderListV2Payload(queryParams),
        });

        return resp;
      } catch (error) {
        message.error('Failed get order list v2');
        return {
          items: [],
          page: queryParams.page,
          per_page: queryParams.per_page,
          total: 0,
          status: {},
          loading: false,
        };
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        items: [],
        page: queryParams.page,
        per_page: queryParams.per_page,
        total: 0,
        status: {},
        loading: true,
      },
    }
  );

  const manualRefreshData = async (options?: { silent: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    await orders.refetch();
    queryClient.invalidateQueries(['order-items-v2']);
    if (!options?.silent) {
      setLoading(false);
    }
  };

  const handleClickEditOrder = async (
    recordId: number,
    doubleToggle = false
  ) => {
    const resp = await getOrderDetail(recordId);
    formOrder.setFieldsValue(formatOrder(resp));
    setOrderId(recordId);
    if (doubleToggle) {
      setOrderDrawer(false);
      await new Promise(r => setTimeout(r, 500));
      setOrderDrawer(true);
    } else {
      setOrderDrawer(true);
    }
  };

  const tableLoading = orders.isLoading || orders.data.loading || loading;

  useEffect(() => {
    const savedColumnsRaw = localStorage.getItem(orderV2ColumnsKey);
    if (savedColumnsRaw) {
      const savedColumns = safelyParseJSON(savedColumnsRaw);
      setColumns(savedColumns);
    }

    if (
      !!queryParams.q_assignee.length ||
      typeof queryParams.is_without_po === 'boolean' ||
      typeof queryParams.is_negative_margin === 'boolean' ||
      typeof queryParams.is_ncpr === 'boolean' ||
      !!queryParams.filter_xero_sync_status ||
      typeof queryParams.filter_exclude_management_fee_only === 'boolean' ||
      typeof queryParams.filter_is_corrected === 'string'
    ) {
      setShowAdvanceFilters(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRecomputeMargin = async (orderIds: number[]) => {
    try {
      if (!orderIds.length) return;
      message.loading({ content: 'Recomputing Margin...', key: 'recompute' });
      await fetchAPI('order/recompute/margin', {
        method: 'POST',
        payload: {
          order_ids: orderIds,
          is_async: false,
        },
      });
      message.success({ content: 'Recomputed Margin!', key: 'recompute' });
      manualRefreshData({ silent: true });
    } catch (error) {
      console.error(error);
      message.error({
        content: 'Failed to recompute margin',
        key: 'recompute',
      });
    }
  };

  const totalPOPayload = useMemo(() => {
    const filters = Object.keys(queryParams).reduce((obj, qpKey) => {
      if (qpKey.startsWith('filter_')) {
        const arr = queryParams[qpKey];
        if (!!arr && !!arr.length) {
          return {
            ...obj,
            [qpKey.replace('filter_', '')]: arr,
          };
        }
      }

      return obj;
    }, {});

    return {
      page: queryParams.page,
      per_page: queryParams.per_page,
      q: queryParams.q,
      q_assignee: queryParams.q_assignee.join(''),
      is_internal_purchase:
        typeof queryParams.is_internal_purchase === 'boolean'
          ? queryParams.is_internal_purchase
          : undefined,
      from_date: !!queryParams.date_start ? queryParams.date_start : undefined,
      to_date: !!queryParams.date_end ? queryParams.date_end : undefined,
      date_type: queryParams.date_type,
      filters,
    };
  }, [queryParams]);

  return (
    <StyledContainer>
      <h2>
        Order Listing V2{' '}
        {tableLoading ? (
          <LoadingOutlined />
        ) : (
          <UndoOutlined onClick={() => manualRefreshData()} />
        )}
      </h2>
      <div
        style={{
          display: 'flex',
          gap: '10px 15px',
          marginBottom: 10,
          flexWrap: 'wrap',
        }}
      >
        {useMemo(() => {
          // Get currency from selected region filter
          const selectedRegion = queryParams.filter_region?.[0];
          const currency = selectedRegion
            ? getCurrencyFromRegion(selectedRegion)
            : undefined;

          return (
            <TotalPOProvider payload={totalPOPayload} currency={currency}>
              <div>
                <Statistic
                  title="Pending Amount to Suppliers"
                  value={formatPrice(
                    get(
                      orders.data.status || {},
                      'pending_amount_to_suppliers',
                      0
                    )
                  )}
                />
              </div>
              <div>
                <OrderTotalPOValues />
              </div>
              <div>
                <OrderTotalPOFulfilledValues />
              </div>
              <div>
                <OrderTotalClientPaidValues />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
                <OrderCorrectedEmailReader />
                <DropdownCurrency />
              </div>
              <div>
                <OrderFXRates />
              </div>
            </TotalPOProvider>
          );
        }, [totalPOPayload, queryParams.filter_region, orders.data.status])}
      </div>
      <div className="filter-and-actions-container">
        <MultiKeywordSearch
          defaultValue={queryParams.q}
          placeholder="Search..."
          onSubmit={value =>
            setQueryParams({ q: value || undefined, page: undefined })
          }
          allowClear
        />
        <Input.Search
          defaultValue={queryParams.q_journal_number}
          placeholder="Search Journal Number..."
          onSearch={value => {
            setQueryParams({ q_journal_number: value, page: undefined });
          }}
          enterButton
          allowClear
          style={{ width: 220 }}
        />
        <Select
          style={{ width: 150 }}
          value={queryParams.date_type}
          onChange={value => setQueryParams({ date_type: value, page: 1 })}
          options={[
            { label: 'Created Date', value: 'created_at' },
            { label: 'Invoice Date', value: 'invoice_date' },
            { label: 'Delivered Date', value: 'completed_date' },
            { label: 'Issue Date', value: 'issue_date' },
            { label: 'Payment Date', value: 'payment_date' },
          ]}
        />
        <DatePicker.RangePicker
          style={{ width: 250 }}
          format={'DD MMM YYYY'}
          onChange={(dates, dateStrings) => {
            if (!dates) {
              setQueryParams({
                page: 1,
                date_start: undefined,
                date_end: undefined,
              });
              return;
            }

            const dateStart = dates[0]
              ? dates[0].utc().format('YYYY-MM-DDTHH:mm:ssZ')
              : undefined;
            const dateEnd = dates[1]
              ? dates[1].utc().format('YYYY-MM-DDTHH:mm:ssZ')
              : undefined;
            setQueryParams({
              page: 1,
              date_start: dateStart,
              date_end: dateEnd,
            });
          }}
          value={[
            queryParams.date_start
              ? moment.utc(queryParams.date_start).local()
              : null,
            queryParams.date_end
              ? moment.utc(queryParams.date_end).local()
              : null,
          ]}
          ranges={{
            Today: [moment(), moment()],
            'This Month': [moment().startOf('month'), moment().endOf('month')],
            'This Year': [moment().startOf('year'), moment().endOf('month')],
          }}
        />
        <Select
          style={{ width: 120 }}
          onChange={value =>
            setQueryParams({
              page: 1,
              is_internal_purchase: !!value ? value === 'internal' : undefined,
            })
          }
          value={
            queryParams.is_internal_purchase === true
              ? 'internal'
              : queryParams.is_internal_purchase === false
              ? 'external'
              : undefined
          }
          placeholder="Type"
          options={[
            { value: 'internal', label: 'Internal' },
            { value: 'external', label: 'External' },
          ]}
          allowClear
        />
        <Select
          style={{ width: 120 }}
          onChange={value =>
            setQueryParams({
              page: 1,
              filter_paid_by: value || '',
            })
          }
          value={queryParams.filter_paid_by || undefined}
          placeholder="Paid Type"
          options={[
            { value: 'SS_PAID', label: 'SS PAID' },
            { value: 'CLIENT_PAID', label: 'CLIENT PAID' },
          ]}
          allowClear
        />
        <Select
          mode="multiple"
          maxTagCount="responsive"
          style={{ width: 150 }}
          onChange={value =>
            setQueryParams({
              page: 1,
              filter_entity_group: value || [],
            })
          }
          value={queryParams.filter_entity_group}
          placeholder="Entity Group"
          loading={entityGroups.isLoading || entityGroups.data.loading}
          options={(entityGroups.data.list || []).map(eg => ({
            value: eg.name,
            label: eg.name,
          }))}
          allowClear
        />
        <DownloadOrders />
        <DownloadOrderItems />
        <UploadSpidReport />

        <Button
          icon={<PlusOutlined />}
          onClick={() => {
            const defaultFormValues = formatOrder(null);
            defaultFormValues.items = [];
            formOrder.setFieldsValue(defaultFormValues);
            setOrderDrawer(true);
            setOrderId(0);
          }}
        >
          Create Order
        </Button>
        <Button onClick={() => setOpenModalDownloadBulkInvoice(true)}>
          Download Bulk Invoice
        </Button>
        <OrderSendReportMenu />
        <Button
          icon={<ProfileOutlined />}
          onClick={() => setInvoiceConsolidationDrawer(true)}
        >
          Invoice Consolidation
        </Button>
        <OrderSendInvoice />
        <OrderModalMapExternalOrder />
        <OrderModalMismapOrder />
        <ReorderColumnsOrderV2
          columns={columns}
          setColumns={payload => {
            localStorage.setItem(orderV2ColumnsKey, JSON.stringify(payload));
            setColumns(payload);
          }}
        />
      </div>
      <div className="advance-filters">
        <div
          className="filter-and-actions-container"
          style={{ display: showAdvanceFilters ? '' : 'none' }}
        >
          <Input.Search
            placeholder="Search Assignee"
            defaultValue={queryParams.q_assignee.join('')}
            allowClear
            enterButton
            onSearch={value =>
              setQueryParams({
                q_assignee: !!value ? [value] : undefined,
                page: undefined,
              })
            }
            style={{ maxWidth: 350 }}
          />
          <Select
            style={{ width: 150 }}
            allowClear
            defaultValue={
              queryParams.is_without_po === true
                ? 'True'
                : queryParams.is_without_po === false
                ? 'False'
                : undefined
            }
            onChange={value => {
              if (typeof value === 'string') {
                setQueryParams({
                  is_without_po: value === 'True',
                  page: undefined,
                });
              } else {
                setQueryParams({ is_without_po: undefined, page: undefined });
              }
            }}
            placeholder="Without PO?"
            options={[
              { value: 'True', label: 'Without PO' },
              { value: 'False', label: 'With PO' },
            ]}
          />
          <Select
            style={{ width: 175 }}
            allowClear
            defaultValue={
              queryParams.is_negative_margin === true
                ? 'True'
                : queryParams.is_negative_margin === false
                ? 'False'
                : undefined
            }
            onChange={value => {
              if (typeof value === 'string') {
                setQueryParams({
                  is_negative_margin: value === 'True',
                  page: undefined,
                });
              } else {
                setQueryParams({
                  is_negative_margin: undefined,
                  page: undefined,
                });
              }
            }}
            placeholder="Filter By Margin +/-"
            options={[
              { value: 'True', label: 'Negative Margin' },
              { value: 'False', label: 'Positive Margin', disabled: true },
            ]}
          />
          <Select
            style={{ width: 120 }}
            allowClear
            defaultValue={
              queryParams.is_ncpr === true
                ? 'True'
                : queryParams.is_ncpr === false
                ? 'False'
                : undefined
            }
            onChange={value => {
              if (typeof value === 'string') {
                setQueryParams({
                  is_ncpr: value === 'True',
                  page: undefined,
                });
              } else {
                setQueryParams({
                  is_ncpr: undefined,
                  page: undefined,
                });
              }
            }}
            placeholder="NCPR"
            options={[
              { value: 'True', label: 'NCPR Order' },
              { value: 'False', label: 'Non NCPR Order', disabled: true },
            ]}
          />
          <Select
            placeholder="Filter Xero Sync Status"
            style={{ width: 200 }}
            allowClear
            defaultValue={queryParams.filter_xero_sync_status || undefined}
            onChange={value =>
              setQueryParams({
                filter_xero_sync_status: value || undefined,
                page: undefined,
              })
            }
          >
            {['SYNCED', 'NOT_SYNCED'].map(stat => (
              <Select.Option key={stat} value={stat}>
                {stat.split('_').join(' ')}
              </Select.Option>
            ))}
          </Select>
          <Select
            placeholder="Filter Management Fee"
            style={{ width: 200 }}
            allowClear
            defaultValue={
              typeof queryParams.filter_exclude_management_fee_only ===
              'boolean'
                ? queryParams.filter_exclude_management_fee_only
                  ? 'exclude'
                  : 'only'
                : undefined
            }
            onChange={value => {
              if (!value) {
                setQueryParams({
                  filter_exclude_management_fee_only: undefined,
                  page: undefined,
                });
              } else {
                setQueryParams({
                  filter_exclude_management_fee_only: value === 'exclude',
                  page: undefined,
                });
              }
            }}
            options={[
              { value: 'exclude', label: 'Exclude Management Fee' },
              { value: 'only', label: 'Only Management Fee', disabled: true },
            ]}
          />
          <Select
            placeholder="Corrected Email Reader"
            style={{ width: 280 }}
            allowClear
            defaultValue={
              !!queryParams.filter_is_corrected ? 'show-only' : undefined
            }
            onChange={value => {
              if (!value) {
                setQueryParams({
                  filter_is_corrected: undefined,
                  page: undefined,
                });
              } else {
                setQueryParams({
                  filter_is_corrected: value,
                  page: undefined,
                });
              }
            }}
            options={[
              {
                value: 'show-only',
                label: "Show Corrected Email Reader's Orders",
              },
            ]}
          />
          <Select
            style={{ width: 200 }}
            allowClear
            defaultValue={
              queryParams.has_change_reason === true
                ? 'True'
                : queryParams.has_change_reason === false
                ? 'False'
                : undefined
            }
            onChange={value => {
              if (typeof value === 'string') {
                setQueryParams({
                  has_change_reason: value === 'True',
                  page: undefined,
                });
              } else {
                setQueryParams({
                  has_change_reason: undefined,
                  page: undefined,
                });
              }
            }}
            placeholder="Filter Remark Changes"
            options={[{ value: 'True', label: 'Has Change Reason' }]}
          />
        </div>
        <p
          className="toggle-text"
          onClick={() => setShowAdvanceFilters(saf => !saf)}
        >
          {showAdvanceFilters ? 'Hide' : 'Show'} Advance Filters
        </p>
      </div>
      <Table
        loading={tableLoading}
        dataSource={orders.data.items}
        bordered
        columns={[
          {
            title: 'Actions',
            width: 160,
            key: 'actions',
            fixed: 'left',
            render: (text, record) => {
              return (
                <OrderActionColumn
                  user={user}
                  record={record}
                  refreshList={opt => manualRefreshData(opt)}
                  handleClickEditOrder={() => handleClickEditOrder(record.id)}
                />
              );
            },
          },
          {
            title: 'SA ID',
            key: 'id',
            // sorter: true,
            width: 85,
            fixed: 'left',
            render: (text, record) => {
              return (
                <>
                  <span className="m-label">SA ID</span>
                  {record?.id}
                </>
              );
            },
          },
          ...implementSortedOrderColumns(
            [
              {
                title: 'Region Identifier',
                key: 'region_ref_number',
                // sorter: true,
                width: 150,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">Region Identifier</span>
                      {record?.region_ref_number ?? '-'}
                    </>
                  );
                },
              },
              {
                title: 'Project Codes',
                key: 'project_code',
                width: 120,
                render: (text, record) => {
                  const code = record?.project_code || '--';
                  return (
                    <>
                      <span className="m-label">Project Codes</span>
                      {code}
                    </>
                  );
                },
              },
              {
                title: 'PO Number',
                key: 'po_reference_number',
                width: 160,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">PO Number</span>
                      {record?.po_reference_number}
                      {record?.external_po_number ? (
                        <p style={{ margin: 0, fontSize: 12 }}>
                          External PO Number:{' '}
                          <b>{record?.external_po_number}</b>
                        </p>
                      ) : null}
                      {record?.journal_number?.length ? (
                        <p style={{ margin: 0, fontSize: 12 }}>
                          Journal Number:{' '}
                          <b>{record.journal_number.join(', ')}</b>
                        </p>
                      ) : null}
                      {record?.is_without_po ? (
                        <p style={{ margin: 0, fontSize: 12 }}>
                          Is Without PO: <b>Yes</b>
                        </p>
                      ) : null}
                      {record?.consolidate_po_number ? (
                        <p style={{ margin: 0, fontSize: 12 }}>
                          Consolidate Invoice Number:{' '}
                          <b>{record?.consolidate_po_number}</b>
                        </p>
                      ) : null}
                      {((record?.entity_name || '')
                        .toLowerCase()
                        .includes('amazon') ||
                        record?.region === 'JP') && (
                        <AmazonOrderDeliveryStatus record={record} />
                      )}
                    </>
                  );
                },
              },
              {
                title: 'Purchase Request',
                key: 'purchase_request_number',
                width: 175,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">Purchase Request</span>
                      {record?.purchase_request_number}
                    </>
                  );
                },
              },
              {
                title: 'Region',
                key: 'region',
                width: 120,
                filters: (user.regions || []).map(item => ({
                  text: item.trim(),
                  value: item.trim(),
                })),
                defaultFilteredValue: queryParams.filter_region,
                render: (text, record) => {
                  const handleRegionSelect = async (newRegion: string) => {
                    if (newRegion === record.region) return;

                    try {
                      const preview = await fetchAPI(
                        `order/preview-region-change/${record.id}`,
                        {
                          method: 'POST',
                          payload: { new_region: newRegion },
                        }
                      );

                      Modal.confirm({
                        title: `Change Region from ${preview.old_region} to ${preview.new_region}?`,
                        width: 720,
                        content: (
                          <div>
                            <Alert
                              type="warning"
                              message={`All order values will be converted from ${preview.old_currency} to ${preview.new_currency}.`}
                              showIcon
                              style={{ marginBottom: 16 }}
                            />
                            <p>
                              <strong>Exchange Rate:</strong> 1{' '}
                              {preview.old_currency} = {preview.exchange_rate}{' '}
                              {preview.new_currency}
                            </p>
                            <Divider />
                            <h4>Item Price Changes</h4>
                            <Table
                              dataSource={preview.items}
                              rowKey="id"
                              size="small"
                              pagination={false}
                              columns={[
                                { title: 'ID', dataIndex: 'id', width: 60 },
                                { title: 'Item Name', dataIndex: 'name' },
                                {
                                  title: `From (${preview.old_currency})`,
                                  dataIndex: 'old_price',
                                  render: (v: number) =>
                                    Number(v).toLocaleString(),
                                },
                                {
                                  title: `To (${preview.new_currency})`,
                                  dataIndex: 'new_price',
                                  render: (v: number) =>
                                    Number(v).toLocaleString(),
                                },
                              ]}
                            />
                            <Divider />
                            <h4>Order Summary</h4>
                            <Table
                              dataSource={[
                                {
                                  key: 'subtotal',
                                  label: 'Subtotal',
                                  from: preview.old_subtotal,
                                  to: preview.new_subtotal,
                                },
                                {
                                  key: 'tax',
                                  label: 'Tax',
                                  from: preview.old_tax,
                                  to: preview.new_tax,
                                },
                                {
                                  key: 'handling_fee',
                                  label: 'Handling Fee',
                                  from: preview.old_handling_fee,
                                  to: preview.new_handling_fee,
                                },
                                {
                                  key: 'total',
                                  label: 'Total',
                                  from: preview.old_total,
                                  to: preview.new_total,
                                },
                              ]}
                              rowKey="key"
                              size="small"
                              pagination={false}
                              columns={[
                                { title: '', dataIndex: 'label' },
                                {
                                  title: `From (${preview.old_currency})`,
                                  dataIndex: 'from',
                                  render: (v: number) =>
                                    Number(v).toLocaleString(),
                                },
                                {
                                  title: `To (${preview.new_currency})`,
                                  dataIndex: 'to',
                                  render: (v: number) =>
                                    Number(v).toLocaleString(),
                                },
                              ]}
                            />
                          </div>
                        ),
                        okText: 'Confirm Change',
                        cancelText: 'Cancel',
                        onOk: async () => {
                          try {
                            const hide = message.loading(
                              'Converting region...',
                              0
                            );
                            await fetchAPI(
                              `order/confirm-region-change/${record.id}`,
                              {
                                method: 'PUT',
                                payload: { new_region: newRegion },
                              }
                            );
                            hide();
                            message.success('Region changed successfully');
                            manualRefreshData({ silent: true });
                          } catch (e) {
                            message.error('Failed to change region');
                          }
                        },
                      });
                    } catch (e) {
                      message.error('Failed to preview region change');
                    }
                  };

                  return (
                    <Select
                      size="small"
                      value={record.region}
                      style={{ width: 85 }}
                      onChange={(val: string) => handleRegionSelect(val)}
                    >
                      {(user.regions || []).map((regi: string) => (
                        <Select.Option key={regi.trim()} value={regi.trim()}>
                          {regi.trim()}
                        </Select.Option>
                      ))}
                    </Select>
                  );
                },
              },
              {
                title: 'Assignee Region Identifier',
                key: 'assignee_region_identifier',
                width: 230,
                filterDropdown: TableFilterSearch,
                filterIcon: filterSearchIcon,
                defaultFilteredValue: queryParams.q_assignee,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">
                        Assignee Region Identifier
                      </span>
                      {!record?.assignee_user_name ? (
                        '-'
                      ) : (
                        <>
                          <b>{record?.assignee_user_name || '-'}</b>
                          <br />
                          Region:
                          <br />
                          <span className="assignee-region-identifier">
                            {record?.assignee_region_identifier || '-'}
                          </span>
                        </>
                      )}
                    </>
                  );
                },
              },
              // {
              //   title: 'Assignee User',
              //   width: 135,
              //   dataIndex: 'assignee_user_name',
              //   hidden: true, // hidden, because only used on download order
              // },
              {
                title: 'Requester Name',
                key: 'requester_name',
                // sorter: true,
                filterDropdown: TableFilterAdvanced({
                  endpoint: 'entity/buyer/list',
                  errorMsg: 'Failed get requester list',
                  searchPlaceholder: 'Search requester',
                  method: 'GET',
                  parser: {
                    value: item => item.id,
                    label: item => (
                      <span>
                        <b>{item.name}</b> <i>({item.email})</i>
                      </span>
                    ),
                  },
                }),
                defaultFilteredValue: queryParams.filter_requester_name,
                width: 155,
                render: (text, record) => {
                  if (!record.requester_name) {
                    return '';
                  }

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span className="m-label">requester Name</span>
                      <span>{record?.requester_name}</span>
                      <span style={{ fontSize: 12 }}>
                        <i>{record?.requester_email}</i>
                      </span>
                    </div>
                  );
                },
              },
              {
                title: 'Recipient Email',
                dataIndex: 'buyer_email',
                key: 'buyer_email',
                // sorter: true,
                width: 155,
                render: text => (
                  <>
                    <span className="m-label">Recipient Email</span>
                    <div
                      style={{
                        minWidth: '120px',
                        wordWrap: 'break-word',
                        wordBreak: 'break-all',
                        fontSize: '12px',
                      }}
                    >
                      {text}
                    </div>
                  </>
                ),
              },
              {
                title: 'Recipient Name', // change from buyer anme to recipient
                key: 'buyer_name',
                // sorter: true,
                filterDropdown: TableFilterAdvanced({
                  endpoint: 'entity/buyer/list',
                  errorMsg: 'Failed get buyer list',
                  searchPlaceholder: 'Search Buyer',
                  method: 'GET',
                  parser: {
                    value: item => item.id,
                    label: item => (
                      <span>
                        <b>{item.name}</b> <i>({item.email})</i>
                      </span>
                    ),
                  },
                }),
                defaultFilteredValue: queryParams.filter_buyer_name,
                width: 150,
                render: (text, record) => {
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span className="m-label">Recipient Name</span>
                      <span>{record?.buyer_name}</span>
                      <span>Ph: {record?.buyer_phone_number}</span>
                    </div>
                  );
                },
              },
              {
                title: 'Entity Name',
                key: 'entity_name',
                // sorter: true,
                filterDropdown: TableFilterAdvanced({
                  endpoint: 'entity/list-for-filter',
                  errorMsg: 'Failed get buyer entity list',
                  searchPlaceholder: 'Search Buyer Entity',
                  method: 'POST',
                  parser: {
                    value: item => item.id,
                    label: item => (
                      <span>
                        {item.name} <i>({item.group || item.id})</i>
                      </span>
                    ),
                  },
                }),
                defaultFilteredValue: queryParams.filter_entity_name,
                width: 155,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">Entity Name</span>
                      {record?.entity_name}
                    </>
                  );
                },
              },
              {
                title: 'Shipping Address',
                dataIndex: 'shipping_address',
                key: 'shipping_address',
                filterDropdown: TableFilterSearch,
                filterIcon: filterSearchIcon,
                width: 250,
                render: (text, record) => (
                  <>
                    <span className="m-label">Shipping Address</span>
                    <div
                      style={{
                        minWidth: '200px',
                      }}
                    >
                      {text}
                    </div>
                    <ShippingAddressFromER
                      shippingAddressFromER={record.shipping_address_from_er}
                    />
                  </>
                ),
              },
              {
                title: 'Exchange Rate',
                width: 135,
                key: 'exchange_rate',
                dataIndex: 'exchange_rate',
              },
              {
                title: 'Total Order',
                key: 'total_price',
                sorter: true,
                defaultSortOrder:
                  queryParams.sort_key === 'total_price'
                    ? queryParams.sort_order === 'descend'
                      ? 'descend'
                      : 'ascend'
                    : null,
                width: 175,
                render: (text, record) => {
                  let totalOrder = record.total_price || 0;

                  if (record.region === 'JP') {
                    totalOrder = Math.round(totalOrder);
                  }

                  return (
                    <>
                      <span className="m-label">Total Order</span>
                      {totalOrder.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 4,
                      })}
                    </>
                  );
                },
              },
              {
                title: 'Total PO Fulfilled',
                key: 'total_price_fulfilled',
                // sorter: true,
                width: 120,
                render: (text, record) => {
                  let totalOrder = record.total_price_fulfilled || 0;

                  if (record.region === 'JP') {
                    totalOrder = Math.round(totalOrder);
                  }

                  return (
                    <>
                      <span className="m-label">Total PO Fulfilled</span>
                      {totalOrder.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 4,
                      })}
                    </>
                  );
                },
              },
              {
                key: 'total_external_remittance',
                title: 'Total External Remittance (Email Reader)',
                // sorter: true,
                width: 200,
                render: (text, record) => {
                  let totalOrder = record.total_external_remittance || 0;

                  if (record.region === 'JP') {
                    totalOrder = Math.round(totalOrder);
                  }

                  return (
                    <OrderExternalRemittance
                      value={totalOrder}
                      orderId={record.id}
                      status={record.payment_status}
                    />
                  );
                },
              },
              {
                title: 'Total Cost',
                width: 120,
                key: 'total_cost',
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">Total Cost</span>
                      {record?.supplier_payment_list
                        ? record?.supplier_payment_list.reduce(function(
                            acc,
                            val
                          ) {
                            return acc + val.total_payable;
                          },
                          0)
                        : 0}
                    </>
                  );
                },
              },
              {
                title: 'Current Tax',
                key: 'current_tax',
                width: 135,
                dataIndex: 'current_tax',
              },
              {
                title: 'All Qty',
                width: 135,
                key: 'all_qty',
                dataIndex: 'all_qty',
              },
              {
                title: 'All Fulfilled Qty',
                width: 135,
                key: 'all_fulfilled_qty',
                dataIndex: 'all_fulfilled_qty',
              },
              {
                title: 'Total Fulfilled Amount',
                width: 135,
                key: 'all_fulfilled_amt',
                dataIndex: 'all_fulfilled_amt',
              },
              {
                title: 'Total Margin',
                width: 150,
                dataIndex: 'total_margin',
                key: 'total_margin',
                // filterDropdown: MarginRangeFilter,
                render: (val, record) => (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <span>{val}</span>
                    <Button
                      type="text"
                      icon={<ReloadOutlined />}
                      size="small"
                      onClick={() => handleRecomputeMargin([record.id])}
                    />
                  </div>
                ),
              },
              {
                title: 'Cumulative Margin (%)',
                key: 'cumulative_margin_percentage',
                width: 180,
                render: (text, record) => {
                  let percentMargin = record.cumulative_margin_percentage || 0;

                  return (
                    <>
                      <span className="m-label">Cumulative Margin (%)</span>
                      {percentMargin}
                    </>
                  );
                },
              },
              {
                title: 'Currency',
                key: 'currency',
                width: 100,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">Currency</span>
                      {`${record?.currency ||
                        getCurrencyFromRegion(record?.region)}`}
                    </>
                  );
                },
              },
              {
                title: 'Operation Status',
                key: 'operation_status',
                // sorter: true,
                width: 225,
                filterDropdown: props => (
                  <TriStateOperationStatusFilter
                    {...props}
                    includeValues={queryParams.filter_operation_status}
                    excludeValues={queryParams.filter_exclude_operation_status}
                    onApply={(include, exclude) =>
                      setQueryParams({
                        page: 1,
                        filter_operation_status: include,
                        filter_exclude_operation_status: exclude,
                      })
                    }
                  />
                ),
                filterIcon: (filtered: boolean) => (
                  <FilterFilled
                    style={{
                      color:
                        filtered ||
                        queryParams.filter_exclude_operation_status?.length
                          ? '#1890ff'
                          : undefined,
                    }}
                  />
                ),
                render: (text, record) => {
                  const operationStatus = record.operation_status
                    ?.split('_')
                    ?.join(' ');
                  return (
                    <>
                      <span className="m-label">Operation Status</span>
                      <Tag color={ColorMap[operationStatus]}>
                        {operationStatus}
                      </Tag>
                      <OrderShipmentStatus
                        record={record}
                        onSuccess={() => manualRefreshData({ silent: true })}
                      />
                    </>
                  );
                },
              },
              {
                title: 'Payment Status',
                key: 'payment_status',
                // sorter: true,
                width: 230,
                filters: paymentStatusFilters.map(item => ({
                  text: item,
                  value: item,
                })),
                defaultFilteredValue: queryParams.filter_payment_status,
                render: (text, record) => (
                  <>
                    <OrderPaymentStatusDropdown
                      record={record}
                      refreshList={async () => {
                        await orders.refetch();
                      }}
                    />
                    <OrderEmailOrderPicker
                      record={record}
                      refreshList={async () => {
                        await orders.refetch();
                      }}
                    />
                  </>
                ),
              },
              {
                title: 'Issue Date',
                key: 'issue_date',
                width: 120,
                // sorter: true,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">Issue Date</span>
                      {formatUTCTimeToLocal(record?.issue_date, 'DD MMM YYYY')}
                    </>
                  );
                },
              },
              {
                title: 'Invoice Date',
                key: 'invoice_date',
                width: 150,
                // sorter: true,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">Invoice Date</span>
                      {formatUTCTimeToLocal(
                        record?.invoice_date,
                        'DD MMM YYYY'
                      )}{' '}
                      <EditInvoiceDateInTable
                        date={record?.invoice_date}
                        orderId={record.id}
                        refreshList={() => manualRefreshData()}
                      />{' '}
                      <OrderInvoiceDateHistory
                        orderId={record.id}
                        date={record?.invoice_date}
                      />
                    </>
                  );
                },
              },
              {
                title: 'DO Date',
                key: 'do_date',
                width: 150,
                // sorter: true,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">DO Date</span>
                      {formatUTCTimeToLocal(
                        record?.do_date,
                        'DD MMM YYYY'
                      )}{' '}
                      <EditDoDateInTable
                        date={record?.do_date}
                        orderId={record.id}
                        refreshList={() => manualRefreshData()}
                      />{' '}
                      <OrderDoDateHistory
                        orderId={record.id}
                        date={record?.do_date}
                      />
                    </>
                  );
                },
              },
              {
                title: 'Payment Date',
                key: 'payment_date',
                // sorter: true,
                width: 150,
                render: (text, record) => {
                  return (
                    <OrderPaymentDate
                      value={record.payment_date}
                      orderId={record.id}
                      status={record.payment_status}
                    />
                  );
                },
              },
              {
                title: 'Delivered Date',
                key: 'completed_at',
                width: 150,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">Delivered Date</span>
                      {formatUTCTimeToLocal(
                        record?.completed_at,
                        'DD MMM YYYY'
                      )}
                    </>
                  );
                },
              },
              {
                title: 'Reason',
                key: 'reason',
                width: 150,
                filterDropdown: TableFilterSearch,
                filterIcon: filterSearchIcon,
                dataIndex: 'reason',
              },
              {
                title: 'SAID Remarks',
                key: 'remarks',
                width: 150,
                filterDropdown: TableFilterSearch,
                filterIcon: filterSearchIcon,
                dataIndex: 'remarks',
                render(value) {
                  const lines = (value || '').split('\n');

                  return (
                    <div style={{ maxHeight: 80, overflowY: 'auto' }}>
                      {lines.map((line, idx) => (
                        <p key={`${line} ${idx}`} style={{ margin: 0 }}>
                          {line}
                        </p>
                      ))}
                    </div>
                  );
                },
              },
              // {
              //   title: 'Reason for Request to Cancel SAID',
              //   filters: reasonOptions.map(item => ({
              //     text: item,
              //     value: item,
              //   })),
              //   dataIndex: 'reason',
              //   render: (value, record) => {
              //     return (
              //       <>
              //         <p style={{ fontSize: '12px' }}>{value ? value : ''}</p>

              //         <ul style={{ padding: '0px', listStyleType: 'none' }}>
              //           {record?.supplier_payment
              //             .filter(item => item?.created_by_id !== 80)
              //             .map(item => (
              //               <li style={{ fontSize: '12px' }}>
              //                 SPID: {item?.id}{' '}
              //                 {item?.reject_reason ? `(${item?.reject_reason})` : '--'}
              //               </li>
              //             ))}
              //         </ul>
              //       </>
              //     );
              //   },
              //   width: 120,
              // },
              {
                title: 'Days to Fulfillment',
                key: 'days_to_fulfill',
                width: 155,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">Days to Fulfillment</span>
                      {record?.days_to_fulfill}
                    </>
                  );
                },
              },
              {
                title: 'Xero Sync',
                width: 170,
                key: 'xero_sync',
                render: (text, record) => {
                  const syncedAt = record.xero_sync_date;
                  const syncedBy = record.xero_sync_user;
                  const syncHistory = record.xero_sync_payload || {};
                  const historyKeys = Object.keys(syncHistory);

                  return (
                    <>
                      <XeroSyncButton
                        id={record.id}
                        xero_invoice_id={record.xero_invoice_id}
                        endpoint={URL_ORDER_SYNC_TO_XERO}
                      />
                      {!!syncedAt && (
                        <>
                          <p style={{ margin: 0 }}>Synced at:</p>
                          <p style={{ margin: 0 }}>
                            {formatUTCTimeToLocal(
                              syncedAt,
                              'HH:mm, DD MMM YYYY'
                            )}
                          </p>
                        </>
                      )}
                      {!!syncedBy && (
                        <>
                          <p style={{ margin: 0 }}>
                            by: {syncedBy.full_name}{' '}
                            <i>({syncedBy.username})</i>
                          </p>
                        </>
                      )}
                      {historyKeys.length > 0 && (
                        <XeroSyncHistory
                          history={syncHistory}
                          placement="rightBottom"
                        />
                      )}
                    </>
                  );
                },
              },
              {
                title: 'Consolidated',
                width: 120,
                key: 'is_consolidated_line_items',
                filters: [
                  {
                    text: 'Yes',
                    value: 'yes',
                  },
                  {
                    text: 'No',
                    value: 'no',
                  },
                ],
                defaultFilteredValue:
                  queryParams.filter_is_consolidated_line_items,
                filterMultiple: false,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">
                        Is Consolidated Line Items
                      </span>
                      <span>
                        Is Consolidated Line Items:{' '}
                        {record?.is_consolidated_line_items ? 'Yes' : 'No'}
                      </span>
                    </>
                  );
                },
              },
              {
                title: 'NCPR',
                key: 'is_ncpr',
                // sorter: true,
                width: 120,
                render: (text, record) => {
                  return (
                    <>{record.is_ncpr ? <span>Yes</span> : <span>No</span>}</>
                  );
                },
              },
              {
                title: 'Created At',
                key: 'created_at',
                // sorter: true,
                width: 120,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">Created At</span>
                      {formatUTCTimeToLocal(record.created_at, 'DD-MM-YYYY')}
                    </>
                  );
                },
              },
              {
                title: 'Created By',
                key: 'created_by_user_name',
                dataIndex: 'created_by_user_name',
                filters: (users.data?.list || []).map(user => ({
                  text: user.full_name,
                  value: user.id,
                })),
                filterSearch: true,
                defaultFilteredValue: queryParams.filter_created_by,
                width: 120,
                render: (text, record) => {
                  return (
                    <>
                      <span className="m-label">Created By</span>
                      {record?.created_by_user_name || '-'}
                    </>
                  );
                },
              },
              // {
              //   title: '',
              //   key: 'xero_sync_status',
              //   width: 1,
              //   render: (text, record) => {
              //     return <></>;
              //   },
              // },
            ],
            columns
          ),
        ]}
        expandable={{
          expandedRowRender: (record, idx, idnt, expanded) => {
            return <OrderItemV2Table orderId={record.id} visible={expanded} />;
          },
          expandedRowKeys: expandedOrders,
          onExpand: (expanded, record) => {
            if (expanded) {
              setExpandedOrders(eo => [...eo, `order-v2-${record.id}`]);
            } else {
              setExpandedOrders(eo =>
                eo.filter(rowKey => rowKey !== `order-v2-${record.id}`)
              );
            }
          },
        }}
        rowKey={record => `order-v2-${record.id}`}
        scroll={{ x: 1200, y: 'calc(100vh - 345px)' }}
        pagination={{
          current: queryParams.page,
          pageSize: queryParams.per_page,
          total: orders.data?.total || 0,
          size: 'small',
          showSizeChanger: true,
          showTitle: true,
          showTotal: (total, range) => {
            return (
              <span>
                Order {range[0]} - {range[1]} of {total}
              </span>
            );
          },
        }}
        onChange={(pagination, filters, sorterResult) => {
          const newQueryParams: Partial<typeof queryParams> = {
            q_assignee: filters.assignee_region_identifier || [],
            filter_region: filters.region || [],
            filter_operation_status: filters.operation_status || [],
            filter_payment_status: filters.payment_status || [],
            filter_is_consolidated_line_items:
              filters.is_consolidated_line_items || [],
            filter_buyer_name: filters.buyer_name || [],
            filter_entity_name: filters.entity_name || [],
            filter_requester_name: filters.requester_name || [],
            filter_shipping_address: filters.shipping_address || [],
            filter_created_by: filters.created_by_user_name || [],
            sort_key: undefined,
            sort_order: undefined,
          };

          const sorter = sorterResult as SorterResult<OrderV2>;
          if (!!sorter.columnKey && !!sorter.order) {
            newQueryParams.sort_key = String(sorter.columnKey);
            newQueryParams.sort_order = sorter.order;
          }

          setQueryParams({
            page: pagination.current === 1 ? undefined : pagination.current,
            per_page:
              pagination.pageSize === 5 ? undefined : pagination.pageSize,
            ...newQueryParams,
          });
        }}
      />

      {/* DRAWER CREATE/EDIT ORDER */}
      <Drawer
        visible={orderDrawer}
        destroyOnClose
        headerStyle={{ position: 'relative' }}
        title={
          <div>
            <span>
              {!!orderId ? `Update Order ${orderId}` : `Create Order`}
            </span>
            <span style={{ position: 'absolute', right: '20px', top: '10px' }}>
              <Button onClick={formOrder.submit} type="primary">
                Save Order
              </Button>
            </span>
          </div>
        }
        placement={isSmallScreen ? 'left' : 'right'}
        width={isSmallScreen ? '90%' : '900px'}
        closable={false}
        onClose={() => {
          formOrder.resetFields();
          setOrderDrawer(false);
        }}
      >
        <OrderCreate
          users={users.data?.list || []}
          form={formOrder}
          onOk={(product: Product) => {
            setOrderDrawer(false);
            manualRefreshData();
          }}
          refreshDetailData={async () => {
            await handleClickEditOrder(orderId, true);
          }}
        />
      </Drawer>

      <Drawer
        visible={openModalDownloadBulkInvoice}
        title="Download Bulk Invoice"
        width={'500px'}
        onClose={() => setOpenModalDownloadBulkInvoice(false)}
      >
        <DownloadBulkInvoice
          closeModal={() => setOpenModalDownloadBulkInvoice(false)}
        />
      </Drawer>

      <Drawer
        title="Invoice Consolidation"
        visible={invoiceConsolidationDrawer}
        width="920"
        onClose={() => setInvoiceConsolidationDrawer(false)}
      >
        <InvoiceConsolidation />
      </Drawer>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  .filter-and-actions-container {
    display: flex;
    margin: 0 0 15px;
    flex-wrap: wrap;
    gap: 15px;

    .ant-input-search {
      width: 250px;
    }
  }

  p.toggle-text {
    color: ${primaryColor};
    width: fit-content;
    margin: 10px 0;

    &:hover {
      cursor: pointer;
      text-decoration: underline;
    }
  }

  .ant-table-tbody > tr .ant-table {
    margin: 10px !important;
  }

  .ant-table-row.ant-table-row-level-0.editable-row {
    background-color: #fff;
  }

  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: #e0e0f8;
  }

  .ant-table {
    tr:nth-child(odd) td {
      background-color: #dfe7ff;
    }
  }

  .m-label {
    display: none;
    font-size: 12px;
    font-weight: bold;
    font-style: italic;
  }

  @media (max-width: 767px) {
    table {
      width: 100%;

      .m-label {
        display: block;
      }
    }
    thead {
      display: none;
    }
    tbody {
      width: 100%;
      display: table-cell;
    }
    tr {
      display: flex;
      width: 100%;
      flex-direction: column;
    }
    td {
      padding: 5px 15px !important;
      font-size: 85%;
    }
  }

  span.assignee-region-identifier {
    font-size: 12px;
  }
`;

const implementSortedOrderColumns = (
  originalColumns: ColumnsType<OrderV2>,
  sortedColumns: OrderColumn[]
) => {
  const activeColumns = sortedColumns.reduce((arr, colObj) => {
    if (!colObj.active) {
      return arr;
    }

    const tableColObj = originalColumns.find(obj => obj.key === colObj.key);
    if (!tableColObj) {
      return arr;
    }

    return [...arr, tableColObj];
  }, []);

  return activeColumns;
};

export default OrderListingV2;
